import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone } from 'lucide-react';
import { businessInfo } from '../mock';

const navLinks = [
  { to: '/about', label: 'About Us', testId: 'nav-about' },
  { to: '/services', label: 'Services', testId: 'nav-services' },
  { to: '/gallery', label: 'Project/Portfolio', testId: 'nav-gallery' },
  { to: '/reviews', label: 'Reviews', testId: 'nav-reviews' },
  { to: '/contact', label: 'Contact', testId: 'nav-contact' },
];

const NavLink = ({ link, isActive, linkColor }) => (
  <Link
    to={link.to}
    data-testid={link.testId}
    className={`${linkColor} transition-colors duration-200 font-medium ${
      isActive ? 'underline underline-offset-8 decoration-2 decoration-blue-500' : ''
    }`}
  >
    {link.label}
  </Link>
);

const CallButton = () => (
  <a
    href={`tel:${businessInfo.phoneRaw}`}
    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
    data-testid="nav-call-now"
  >
    <Phone size={18} />
    Call Now
  </a>
);

const BookOnlineButton = () => (
  <Link
    to="/booking"
    data-testid="nav-book-online-cta"
    className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 rounded-lg font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
  >
    Book Online
  </Link>
);

const MobileMenuButton = ({ open, onToggle, transparentMode }) => (
  <button
    onClick={onToggle}
    className={(transparentMode ? 'text-white' : 'text-slate-900') + ' p-2'}
    data-testid="mobile-menu-toggle"
    aria-label="Toggle menu"
  >
    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      {open ? (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      ) : (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
      )}
    </svg>
  </button>
);

const MobileMenu = ({ isActive }) => (
  <div className="md:hidden bg-white shadow-lg border-t border-slate-100">
    <div className="px-4 pt-2 pb-6 space-y-1">
      {navLinks.map((link) => (
        <Link
          key={link.to}
          to={link.to}
          className={`block w-full text-left py-3 transition-colors duration-200 ${
            isActive(link.to) ? 'text-blue-600 font-semibold' : 'text-slate-700 hover:text-blue-600'
          }`}
        >
          {link.label}
        </Link>
      ))}
      <Link
        to="/booking"
        className="flex items-center justify-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg font-semibold mt-4"
      >
        Book Online
      </Link>
      <a
        href={`tel:${businessInfo.phoneRaw}`}
        className="flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold mt-2"
      >
        <Phone size={18} />
        Call Now
      </a>
    </div>
  </div>
);

const useNavbarScroll = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 50);
  }, [setIsScrolled]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return isScrolled;
};

const NavbarLogo = () => (
  <Link to="/" className="flex-shrink-0 flex items-center gap-4">
    <img
      src={businessInfo.logo}
      alt={businessInfo.name}
      className="h-28 w-28 md:h-40 md:w-40 object-contain"
    />
  </Link>
);

const DesktopNav = ({ isActive, linkColor }) => (
  <div className="hidden md:flex items-center space-x-8">
    {navLinks.map((link) => (
      <NavLink key={link.to} link={link} isActive={isActive(link.to)} linkColor={linkColor} />
    ))}
  </div>
);

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isScrolled = useNavbarScroll();

  const isHomePage = location.pathname === '/';
  const transparentMode = isHomePage && !isScrolled;

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname, setIsMobileMenuOpen]);

  const linkColor = transparentMode
    ? 'text-white hover:text-blue-300'
    : 'text-slate-700 hover:text-blue-600';

  const isActive = (to) => location.pathname === to;

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed left-0 right-0 z-50 transition-all duration-300 top-0 md:top-11 ${
        transparentMode ? 'bg-transparent' : 'bg-white/95 backdrop-blur-md shadow-md border-b border-slate-100'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-32 md:h-40">
          <div className="flex items-center gap-10">
            <NavbarLogo />
            <DesktopNav isActive={isActive} linkColor={linkColor} />
          </div>
          <div className="hidden md:flex items-center gap-3">
            <BookOnlineButton />
            <CallButton />
          </div>
          <div className="md:hidden">
            <MobileMenuButton
              open={isMobileMenuOpen}
              onToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              transparentMode={transparentMode}
            />
          </div>
        </div>
      </div>
      {isMobileMenuOpen && <MobileMenu isActive={isActive} />}
    </nav>
  );
};

export default Navbar;
